<template>
    <v-form>
        <v-row v-if="loading" dense>
            <v-sheet class="full-width d-flex justify-center py-2">
                <v-progress-linear color="primary" indeterminate class="mb-0" height="3px" />
            </v-sheet>
        </v-row>
        <template v-else>
            <v-row dense class="mb-4">
                <v-col>
                    <LocaleSwitcher @change="changeActiveLangCode" />
                </v-col>
            </v-row>
            <images-upload-form
                v-model="excursionImages"
                :images-in-a-row="6"
                :init-data="useInitData"
                :order-editable="false"
                @delete-image="onImageDelete"
            >
                <template v-if="excursionImages" v-slot:top-fields="{image}">
                    <v-checkbox
                        v-model="image.mainImage"
                        :model-value="mainImageUrl === image.url"
                        hide-details
                        :label="mainImageUrl === image.url ? $t('main_image') : ''"
                        density="compact"
                        class="pb-1"
                        :data-cy="`main-image-${image.id}`"
                        :disabled="image.toDelete"
                        @update:model-value="changeMainImage(image)"
                    />
                </template>
                <template v-if="excursionImages" v-slot:fields="{image}">
                    <TextFieldLocale
                        v-model="image.title[activeLangCode]"
                        :active-lang-code="activeLangCode"
                        :data-cy="`title-${image.id}`"
                        :disabled="image.toDelete"
                    />
                </template>
            </images-upload-form>
            <v-row dense class="px-3 pb-4">
                <v-btn color="primary" class="save-btn mr-4" :loading="isSaving" @click="save">
                    {{ $t('save') }}
                </v-btn>
                <v-btn
                    class="cancel-btn"
                    color="white"
                    data-cy="cancel"
                    :to="{name: 'supplierProducts', hash: '#activities'}"
                >
                    {{ $t('cancel') }}
                </v-btn>
            </v-row>
        </template>
    </v-form>
</template>

<script setup lang="ts">
    import ImagesUploadForm from '~src/components/account/snippets/ImagesUploadForm.vue'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'
    import {clone} from '@/utils/helpers'
    import type {Ref} from 'vue'

    const activeLangCode = ref<string>('en')

    const isSaving = ref<boolean>(false)
    const excursionImages: Ref<any[]> = ref([])
    const excursion: Ref = ref({})

    const app = useNuxtApp()
    const {t} = useI18n()

    const props = defineProps<{
        excursionInfo: any
        loading?: boolean
    }>()

    const emit = defineEmits<{
        (e: 'updated'): void
    }>()

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }

    const initImages = () => {
        excursionImages.value = excursion.value.images || []
    }

    watch(
        () => props.excursionInfo,
        () => {
            excursion.value = clone(props.excursionInfo)
            initImages()
        },
        {immediate: true}
    )

    onMounted(async () => {
        activeLangCode.value = app.$i18n.locale.value
    })

    const mainImageUrl = computed({
        get: () => excursion.value.mainImage,
        set: value => (excursion.value.mainImage = value),
    })

    const useInitData = computed(() => {
        return {
            title: {},
            mainImage: !excursionImages.value?.length,
        }
    })

    const save = async () => {
        try {
            isSaving.value = true
            const data = clone(excursion.value)

            await app.$api.excursions.put({
                ...data,
                images: excursionImages.value
                    .filter(item => !item.toDelete)
                    .map(({id, title, url, fileStream}) => {
                        if (fileStream) {
                            return {title, url: fileStream}
                        }

                        return {id, title, url}
                    }),
            })
            emit('updated')
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isSaving.value = false
        }
    }

    const changeMainImage = image => {
        mainImageUrl.value = image.url
    }

    const onImageDelete = image => {
        if (image.url === mainImageUrl.value) {
            mainImageUrl.value = null
        }
    }
</script>
